import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';
import { rgba } from 'polished';
import { Box, BoxProps, Flex } from 'reflexbox';
import { useApolloClient } from '@apollo/client';

import { Account, LegacyAccount, useKeychain } from '@treams/auth';
import {
	AddIcon,
	ChevronDownIcon,
	ConversationIcon,
	DialogueIcon,
	DraftsIcon,
	FolderIcon,
	GearIcon,
	GiftIcon,
	HelpIcon,
	LibraryIcon,
	LogOutIcon,
	ReorderIcon,
	SearchIcon,
	SendIcon,
	TargetIcon,
} from '@treams/treams-icons';
import { Link, useHistory } from '@treams/treams-routes';
import {
	Avatar,
	Button,
	ContextMenu,
	Highlight,
	Input,
	Paragraph,
	Portal,
	Skeleton,
	Stat,
	Subheading,
} from '@treams/treams-ui';

import * as graphql from 'generated/graphql';

import Logo from 'assets/logo.svg';
import useSearch from 'packages/kakapo-ui-application/hooks/useSearch';
import useFeature from 'packages/kakapo-ui-application/hooks/useFeature';
import useViewerRoles from 'packages/kakapo-ui-application/hooks/useUserRoles';
import { useHelicopter } from './HelicopterContext';

const Container = styled(Flex)`
	height: 80px;
	border-bottom: 1px solid ${(props) => props.theme.colors.lightBlue};
`;

const OnlyScreen = styled(Box)`
	@media print {
		display: none;
	}
`;

const Overlay = styled(Box)`
	position: absolute;
	top: 0px;
	left: 0;
	width: 100%;
	height: 100%;
	background: ${(props) => rgba(props.theme.colors.slateBlue, 0.5)};
	z-index: 2;
`;

const Results = styled(Box)`
	position: absolute;
	left: 270px;
	width: 500px;
	margin-top: 80px;
	margin-left: 16px;
	background: white;
	border-radius: 6px;
	box-shadow:
		0px 2px 4px ${(props) => rgba(props.theme.colors.slateBlue, 0.25)},
		0px 4px 8px ${(props) => rgba(props.theme.colors.slateBlue, 0.25)};
	overflow: hidden;
`;

const ResultLink = styled(Link)`
	color: black;
	text-decoration: none;
`;

const Result = styled(Flex)<{ highlighted?: boolean }>`
	align-items: center;

	${(props) => props.highlighted && `background: ${props.theme.colors.lightBlueLight};`}

	&:hover {
		background: ${(props) => props.theme.colors.lightBlueLight};
	}

	&:not(:last-child) {
		border-bottom: 1px solid ${(props) => props.theme.colors.lightBlue};
	}
`;

const LogoContainer = styled(Box)`
	transition: 0.4s ease-out opacity;
	svg {
		display: block;
		width: 140px;
		height: 50px;
	}
	img {
		display: block;
		max-width: 140px;
		max-height: 40px;
		object-fit: contain;
		object-position: left center;
	}
	&:hover {
		opacity: 0.75;
		&:hover {
			opacity: 0.5;
		}
	}
`;

const ProfileContainer = styled(Flex)`
	padding: 6px;
	border-radius: 6px;
	align-items: center;
	transition: 0.2s ease-out all;
	cursor: pointer;

	${Button.Styles.Secondary}

	svg {
		width: 16px;
		height: 16px;
		margin: 0px 4px;

		* {
			fill: ${(props) => props.theme.colors.secondary};
		}
	}
`;

const KeyBinding = styled(Box)`
	flex-grow: 0;
	flex-shrink: 0;
	border: 1px solid ${(props) => props.theme.colors.lightBlue};
	background: ${(props) => props.theme.colors.white};
	padding: 4px;
	margin: -4px 0px;
	font-size: 12px;
	vertical-align: middle;
	border-radius: 4px;
`;

const KeyBindingShortcut = styled.span`
	font-size: 14px;
	font-weight: bold;
	vertical-align: middle;
`;

function AccountLink(props: { account: Account | LegacyAccount }) {
	const client = useApolloClient();
	const keychain = useKeychain();
	const helicopter = useHelicopter();

	const query = graphql.useUserQuery({
		variables: { id: 'userID' in props.account ? props.account.userID : props.account.id },
		context: { account: props.account.id },
		fetchPolicy: 'no-cache',
		nextFetchPolicy: 'no-cache',
	});

	if (query.data) {
		return (
			<ContextMenu.Item
				icon={<Avatar size={32} user={query.data.user as graphql.User} />}
				title={`${query.data.user.firstName} ${query.data.user.lastName}`}
				onClick={async () => {
					await client.clearStore();
					keychain.switch(props.account as Account);
					helicopter.disable();

					await client.resetStore();
				}}
			/>
		);
	} else {
		return (
			<Flex alignItems="center" p={2}>
				<Avatar.Skeleton size={32} mr={2} />
				<Paragraph.Skeleton lines={1} />
			</Flex>
		);
	}
}

const Profile = Skeleton(
	function Profile(props: BoxProps & { user: graphql.User; switcher?: boolean }) {
		const keychain = useKeychain();
		const { t } = useTranslation();
		const roles = useViewerRoles();
		const helicopter = useHelicopter();

		const multi = (roles.isWorkspaceSuperuser || keychain.all().length > 1) && !keychain.isLegacy();

		return (
			<ContextMenu.Target
				menu={
					<ContextMenu width={multi ? 300 : 200}>
						{multi && (
							<>
								{keychain.all().map((account, i) => (
									<AccountLink key={i} account={account} />
								))}
								<Link
									to={`/authentication?m`}
									onClick={() => {
										helicopter.disable();
									}}
								>
									<ContextMenu.Item icon={<AddIcon />} title={t('menu.context.addAccount')} />
								</Link>
								<ContextMenu.Separator />
							</>
						)}
						{!props.switcher && (
							<>
								<Link to={`/users/${props.user.id}/personal-files`}>
									<ContextMenu.Item
										icon={<FolderIcon />}
										title={t('menu.context.viewPersonalFiles')}
									/>
								</Link>
								<Link to={`/personal-settings`}>
									<ContextMenu.Item
										icon={<GearIcon />}
										title={t('menu.context.personalSettings')}
									/>
								</Link>
								<ContextMenu.Separator />
							</>
						)}
						<Link to="/logout">
							<ContextMenu.Item icon={<LogOutIcon />} title={t('menu.context.logOut')} />
						</Link>
					</ContextMenu>
				}
			>
				<ProfileContainer {...props}>
					<Avatar user={props.user} size={32} mr={2} />
					<Paragraph>
						{props.user.firstName} {props.user.lastName}
					</Paragraph>
					<ChevronDownIcon />
				</ProfileContainer>
			</ContextMenu.Target>
		);
	},
	function ProfileSkeleton(props: BoxProps) {
		return (
			<ProfileContainer {...props}>
				<Avatar.Skeleton size={32} mr={2} />
				<Paragraph.Skeleton lines={1} width={120} />
			</ProfileContainer>
		);
	},
);

function Search() {
	const { t } = useTranslation();

	const history = useHistory();

	const [query, setQuery] = useState('');
	const [focus, setFocus] = useState(false);
	const [index, setIndex] = useState<number | undefined>(undefined);
	const now = useRef(Date.now()).current;

	const filters = [
		'isArchived:false',
		`activatedAt <= ${now}`,
		'(' + ['deactivatedAt = 0', `deactivatedAt > ${now}`].join(' OR ') + ')',
	].join(' AND ');

	const { results, rawLoading } = useSearch(query, {
		filters,
		limit: 5,
		skip: query.length === 0,
		excludeViewer: true,
	});

	function reset() {
		setFocus(false);
		setQuery('');
		setIndex(undefined);
		(document.activeElement as HTMLElement)?.blur();
	}

	useEffect(() => {
		if (!rawLoading) {
			setIndex(undefined);
		}
	}, [rawLoading]);

	const isRawLoading = rawLoading ? rawLoading : undefined;
	return (
		<>
			<div style={focus ? { position: 'relative', zIndex: 3 } : undefined}>
				<Input
					search
					loading={isRawLoading}
					placeholder={t('menu.search.placeholder')}
					icon={<SearchIcon />}
					value={query}
					onKeyDown={(event) => {
						if (event.key === 'Escape') {
							reset();
						} else if (event.key === 'ArrowDown') {
							setIndex((index) => ((index ?? -1) + 1) % results.length);
							event.preventDefault();
						} else if (event.key === 'ArrowUp') {
							setIndex((index) => ((index ?? -1) - 1 + results.length) % results.length);
							event.preventDefault();
						} else if (event.key === 'Enter') {
							if (index !== undefined) {
								history.push(`/users/${results[index].id}`);
								reset();
							}
						}
					}}
					onFocus={() => setFocus(true)}
					onBlur={() => setFocus(false)}
					onChange={setQuery}
				/>
			</div>
			{focus && (
				<Portal>
					<Overlay>
						<Results>
							{results.map((user, i) => (
								<ResultLink key={user.id} to={`/users/${user.id}`}>
									<Result
										p={3}
										onMouseEnter={() => setIndex(i)}
										onMouseDown={(event) => event.preventDefault()}
										onClick={reset}
										highlighted={index === i}
									>
										<Box mr={2} flexGrow={0} flexShrink={0}>
											<Avatar user={user} size={24} />
										</Box>
										<Highlight query={query} flexGrow={1} flexShrink={1}>
											{user.firstName} {user.lastName}
										</Highlight>
										{index === i && (
											<KeyBinding>
												Enter <KeyBindingShortcut>↵</KeyBindingShortcut>
											</KeyBinding>
										)}
									</Result>
								</ResultLink>
							))}
						</Results>
					</Overlay>
				</Portal>
			)}
		</>
	);
}

function AddMenu() {
	const { t } = useTranslation();

	const hasCompliments = useFeature(graphql.FeatureName.Compliments);
	const hasGoals = useFeature(graphql.FeatureName.Goals);
	const hasFeedback = useFeature(graphql.FeatureName.Feedback);
	const canAskFeedback = useFeature(graphql.FeatureName.FeedbackAdhoc);
	const canGiveFeedback = useFeature(graphql.FeatureName.FeedbackSpontaneous);
	const hasConversations = useFeature(graphql.FeatureName.Evaluations);
	const hasNotes = useFeature(graphql.FeatureName.Notes);
	const hasLearning = useFeature(graphql.FeatureName.Learning);

	return (
		<ContextMenu.Target
			menu={
				<ContextMenu>
					{hasGoals && (
						<Link to="/goals/new">
							<ContextMenu.Item title={t('menu.actions.createGoal')} icon={<TargetIcon />} />
						</Link>
					)}
					{hasFeedback && canAskFeedback && (
						<Link to="/feedback/request-from-moment">
							<ContextMenu.Item title={t('menu.actions.askFeedback')} icon={<DialogueIcon />} />
						</Link>
					)}
					{hasFeedback && canGiveFeedback && (
						<Link to="/feedback/send">
							<ContextMenu.Item title={t('menu.actions.giveFeedback')} icon={<SendIcon />} />
						</Link>
					)}
					{hasCompliments && (
						<Link to="/feedback/compliments/new">
							<ContextMenu.Item title={t('menu.actions.giveCompliment')} icon={<GiftIcon />} />
						</Link>
					)}
					{hasConversations && (
						<Link to="/conversations/request-from-moment">
							<ContextMenu.Item
								title={t('menu.actions.requestConversation')}
								icon={<ConversationIcon />}
							/>
						</Link>
					)}
					{hasNotes && (
						<Link to="/notes/new">
							<ContextMenu.Item title={t('menu.actions.createNote')} icon={<DraftsIcon />} />
						</Link>
					)}
					{hasLearning && (
						<Link to="/learning/events/new">
							<ContextMenu.Item
								title={t('menu.actions.createLearningEvent')}
								icon={<LibraryIcon />}
							/>
						</Link>
					)}
				</ContextMenu>
			}
		>
			<Stat borderless icon={<AddIcon />} tidy />
		</ContextMenu.Target>
	);
}

export function AccountSwitcher() {
	const query = graphql.useViewerQuery();

	return query.data ? (
		<Profile user={query.data.viewer as graphql.User} switcher />
	) : (
		<Profile.Skeleton />
	);
}

const DesktopMenuContainer = styled(Box)`
	background-color: ${(props) => props.theme.colors.white};
`;

function DesktopMenu(props: { plain?: boolean }) {
	const query = graphql.useViewerQuery();

	return (
		<DesktopMenuContainer>
			<Container p={3} alignItems="center">
				{!props.plain && (
					<>
						<Box flexBasis={300} flexGrow={1} maxWidth={500} display={['none', 'none', 'block']}>
							<Search />
						</Box>
						<Box flexGrow={1} />
						<Flex pr={4} pl={4}>
							<AddMenu />
							<a href="https://guides.ontreams.com/nl/" target="_blank" rel="noreferrer">
								<Stat ml={2} icon={<HelpIcon />} borderless tidy />
							</a>
							<Link to="/personal-settings">
								<Stat ml={2} icon={<GearIcon />} borderless tidy />
							</Link>
						</Flex>
						{query.data ? (
							<Profile user={query.data.viewer as graphql.User} />
						) : (
							<Profile.Skeleton />
						)}
					</>
				)}
			</Container>
		</DesktopMenuContainer>
	);
}

function DesktopMenuSkeleton() {
	return (
		<DesktopMenuContainer>
			<Container p={3} alignItems="center">
				<Box flexBasis={300} flexGrow={1} maxWidth={500} display={['none', 'none', 'block']}>
					<Input.Skeleton search />
				</Box>
				<Box flexGrow={1} />
				<Flex pr={4} pl={4}>
					<Stat borderless icon={<AddIcon />} tidy />
					<Stat ml={2} icon={<HelpIcon />} borderless tidy />
					<Stat ml={2} icon={<GearIcon />} borderless tidy />
				</Flex>
				<Profile.Skeleton />
			</Container>
		</DesktopMenuContainer>
	);
}

function MobileMenu(props: { onOpen: () => void }) {
	const theme = useTheme();

	return (
		<Container py={2} px={3} alignItems="center" justifyContent="space-between">
			{theme.images.logo ? (
				<Link to="/">
					<LogoContainer flexGrow={0} flexShrink={0}>
						{theme.images.logo.startsWith('/') ? (
							<Logo />
						) : (
							<img src={theme.images.logo} alt="logo" />
						)}
					</LogoContainer>
				</Link>
			) : (
				<Skeleton.Box width={140} height={50} />
			)}
			<Flex alignItems="center" onClick={props.onOpen}>
				<Box mr={1}>
					<Subheading>Menu</Subheading>
				</Box>
				<Box size={32}>
					<ReorderIcon />
				</Box>
			</Flex>
		</Container>
	);
}

export default function Menu(props: { plain?: boolean; onOpen?: () => void }) {
	return (
		<OnlyScreen>
			<Box display={['none', 'none', 'block']}>
				<DesktopMenu plain={props.plain} />
			</Box>

			{props.onOpen && (
				<Box display={['block', 'block', 'none']}>
					<MobileMenu onOpen={props.onOpen} />
				</Box>
			)}
		</OnlyScreen>
	);
}

export function MenuSkeleton() {
	return (
		<OnlyScreen>
			<Box display={['none', 'none', 'block']}>
				<DesktopMenuSkeleton />
			</Box>
		</OnlyScreen>
	);
}

Menu.Skeleton = MenuSkeleton;
